import { Button } from '@rental/ui'
import { Eraser } from 'lucide-react'
import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'

/** x and y in ten-thousandths of the pad, t in ms from the first touch, p the pressure in thousandths. */
export type Point = [number, number, number, number]
export type Pointer = 'pen' | 'touch' | 'mouse'

/** What a pad hands over: the strokes with pen pressure and timing, and the image it showed. */
export interface Drawn {
  width: number
  height: number
  strokes: { pointer: Pointer; points: Point[] }[]
  /** The pad's PNG, base64 without the data: prefix. */
  png: string
}

/** The server takes at most this many points a signature (SignatureInput). */
const MAX_POINTS = 8_000
const MAX_STROKE_POINTS = 4_000
const SCALE = 10_000

const clamp = (value: number) => Math.min(SCALE, Math.max(0, Math.round(value)))
const pointerOf = (type: string): Pointer =>
  type === 'pen' ? 'pen' : type === 'mouse' ? 'mouse' : 'touch'
/** The pen's line follows its pressure; a finger or a mouse draw evenly. */
const inkWidth = (pointer: Pointer, pressure: number) =>
  pointer === 'pen' ? 1 + 3 * (pressure || 0.5) : 2.5

/**
 * CTR-2: a signature box for finger or Apple Pencil. It records every point
 * with its time and pressure (the pen reports it; a finger or a mouse report a
 * constant) and draws as it goes, the line thicker where the pen presses.
 * Colours come from the page's tokens. Signing is a pointer gesture by nature
 * (WCAG 2.5.1 allows it where essential); the box is named and says whether it
 * holds a signature, and the counter keeps the printed contract as the other way.
 */
export function SignaturePad({
  label,
  hint,
  emptyText,
  signedText,
  clearText,
  onChange,
}: {
  label: string
  hint: string
  emptyText: string
  signedText: string
  clearText: string
  onChange: (drawn: Drawn | null) => void
}) {
  const id = useId()
  const canvas = useRef<HTMLCanvasElement>(null)
  const strokes = useRef<Drawn['strokes']>([])
  const startedAt = useRef<number | null>(null)
  const last = useRef<{ x: number; y: number } | null>(null)
  const [signed, setSigned] = useState(false)
  // S9: 2:1 on a phone (326 x 163 at 390 px, not 326 x 109), 3:1 from Tailwind's sm up. Chosen
  // once, as the pad appears: the strokes are kept relative to the box, so a shape that changed
  // with a rotation would stretch the signature already drawn, and the image signed with it.
  const [shape] = useState(() =>
    window.matchMedia?.('(min-width: 40rem)').matches === false ? 'aspect-2/1' : 'aspect-3/1',
  )
  // The latest callback, without making the drawing depend on the parent's renders.
  const changed = useRef(onChange)
  changed.current = onChange

  const total = () => strokes.current.reduce((sum, stroke) => sum + stroke.points.length, 0)

  const context = useCallback(() => {
    const element = canvas.current
    const drawing = element?.getContext('2d')
    if (!element || !drawing) return null
    drawing.lineCap = 'round'
    drawing.lineJoin = 'round'
    // The ink is the page's text colour, read from its token.
    drawing.strokeStyle = getComputedStyle(element).color
    return { element, drawing }
  }, [])

  /** What the pad holds now, for the parent; null until something is drawn. */
  const emit = useCallback(() => {
    const element = canvas.current
    const points = strokes.current.reduce((sum, stroke) => sum + stroke.points.length, 0)
    if (!element || points < 2) {
      setSigned(false)
      changed.current(null)
      return
    }
    const box = element.getBoundingClientRect()
    setSigned(true)
    changed.current({
      width: Math.max(100, Math.round(box.width)),
      height: Math.max(50, Math.round(box.height)),
      strokes: strokes.current.map((stroke) => ({ ...stroke, points: [...stroke.points] })),
      png: element.toDataURL('image/png').replace(/^data:image\/png;base64,/, ''),
    })
  }, [])

  /**
   * Sized to its box at the screen's pixel density, so the PNG is as sharp as
   * what was seen. Resizing blanks a canvas, so the strokes are drawn again
   * from their points (a rotated iPad keeps the signature).
   */
  const fit = useCallback(() => {
    const element = canvas.current
    const drawing = element?.getContext('2d')
    if (!element || !drawing) return
    const box = element.getBoundingClientRect()
    const ratio = window.devicePixelRatio || 1
    const pixels = { width: Math.round(box.width * ratio), height: Math.round(box.height * ratio) }
    if (element.width === pixels.width && element.height === pixels.height) return
    element.width = pixels.width
    element.height = pixels.height
    drawing.scale(ratio, ratio)
    drawing.lineCap = 'round'
    drawing.lineJoin = 'round'
    drawing.strokeStyle = getComputedStyle(element).color
    for (const stroke of strokes.current) {
      let from: { x: number; y: number } | null = null
      for (const [x, y, , p] of stroke.points) {
        const to = { x: (x / SCALE) * box.width, y: (y / SCALE) * box.height }
        drawing.lineWidth = inkWidth(stroke.pointer, p / 1_000)
        drawing.beginPath()
        drawing.moveTo((from ?? to).x, (from ?? to).y)
        drawing.lineTo(to.x, to.y)
        drawing.stroke()
        from = to
      }
    }
    if (strokes.current.length > 0) emit()
  }, [emit])

  useEffect(() => {
    fit()
    const observer = new ResizeObserver(() => fit())
    if (canvas.current) observer.observe(canvas.current)
    return () => observer.disconnect()
  }, [fit])

  function record(event: PointerEvent, stroke: Drawn['strokes'][number]) {
    const found = context()
    if (!found || total() >= MAX_POINTS || stroke.points.length >= MAX_STROKE_POINTS) return
    const box = found.element.getBoundingClientRect()
    const x = event.clientX - box.left
    const y = event.clientY - box.top
    startedAt.current ??= event.timeStamp
    const pressure = event.pointerType === 'mouse' ? 0.5 : event.pressure
    stroke.points.push([
      clamp((x / box.width) * SCALE),
      clamp((y / box.height) * SCALE),
      Math.max(0, Math.round(event.timeStamp - startedAt.current)),
      Math.min(1_000, Math.max(0, Math.round(pressure * 1_000))),
    ])
    const from = last.current ?? { x, y }
    found.drawing.lineWidth = inkWidth(stroke.pointer, pressure)
    found.drawing.beginPath()
    found.drawing.moveTo(from.x, from.y)
    found.drawing.lineTo(x, y)
    found.drawing.stroke()
    last.current = { x, y }
  }

  function begin(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (event.button !== 0 && event.pointerType === 'mouse') return
    event.currentTarget.setPointerCapture(event.pointerId)
    last.current = null
    const stroke = { pointer: pointerOf(event.pointerType), points: [] as Point[] }
    strokes.current.push(stroke)
    record(event.nativeEvent, stroke)
  }

  function move(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return
    const stroke = strokes.current.at(-1)
    if (!stroke) return
    // Apple Pencil reports far more often than the screen draws: keep every sample.
    const samples = event.nativeEvent.getCoalescedEvents?.() ?? []
    for (const sample of samples.length > 0 ? samples : [event.nativeEvent]) record(sample, stroke)
  }

  function end(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    last.current = null
    strokes.current = strokes.current.filter((stroke) => stroke.points.length > 0)
    emit()
  }

  function clear() {
    const found = context()
    if (found) found.drawing.clearRect(0, 0, found.element.width, found.element.height)
    strokes.current = []
    startedAt.current = null
    emit()
  }

  return (
    <fieldset className="flex min-w-0 flex-col gap-2">
      <legend id={`${id}-label`} className="mb-2 font-bold">
        {label}
      </legend>
      <p id={`${id}-hint`} className="text-sm text-text-muted">
        {hint}
      </p>
      {/* An image of what is drawn so far, named; drawing itself needs a finger or the pen. */}
      <canvas
        ref={canvas}
        role="img"
        aria-label={signed ? signedText : emptyText}
        aria-describedby={`${id}-hint`}
        data-testid="signature-pad"
        className={`${shape} w-full touch-none rounded-sm border-2 border-text-muted bg-bg text-text`}
        onPointerDown={begin}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
      />
      <div className="flex items-center justify-between gap-4">
        <p role="status" className="text-sm text-text-muted">
          {signed ? signedText : emptyText}
        </p>
        <Button variant="link" onClick={clear} aria-describedby={`${id}-label`}>
          <Eraser aria-hidden="true" className="size-4" />
          {clearText}
        </Button>
      </div>
    </fieldset>
  )
}
